import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Collapse,
  Alert,
  AlertTitle,
  Stack,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import CardContainer from '../../../shared/components/CardContainer';
import CardHeader from '../../../shared/components/CardHeader';
import ExpSnackbarAlerts from '../../../shared/components/display/ExpSnackbarAlerts';
import DoubleConfirmModal from '../../../shared/components/modals/DoubleConfirmModal';
import useUserRoles from '../../../shared/hooks/useUserRoles';
import useDateFormat from '../../../shared/hooks/useDateFormat';
import RequestBodyButton from './components/RequestBodyButton';
import {
  getHealthMonitorLogs,
  getHealthMonitorSummary,
  postHealthMonitorCleanup,
} from '../services/HealthMonitorRepository';

// --- Sub-components ---

/**
 * KPI card for the summary row.
 */
const KpiCard = ({ title, value, color, icon }) => (
  <CardContainer>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box>
        <Typography variant='body2' color='text.secondary'>
          {title}
        </Typography>
        <Typography variant='h4' sx={{ fontWeight: 600, mt: 0.5 }}>
          {value}
        </Typography>
      </Box>
      <Box sx={{ color, opacity: 0.7 }}>{icon}</Box>
    </Box>
  </CardContainer>
);

/**
 * Status chip renderer.
 */
const StatusChip = ({ status }) => {
  const config = {
    SUCCESS: {
      color: 'success',
      label: 'Success',
      icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
    },
    FAILURE: {
      color: 'error',
      label: 'Failure',
      icon: <ErrorIcon sx={{ fontSize: 16 }} />,
    },
    PARTIAL_SUCCESS: {
      color: 'warning',
      label: 'Partial',
      icon: <WarningIcon sx={{ fontSize: 16 }} />,
    },
    ACTION_NEEDED: {
      color: 'info',
      label: 'Action Needed',
      icon: <InfoIcon sx={{ fontSize: 16 }} />,
    },
    STAGED: {
      color: 'success',
      label: 'Staged',
      icon: <HourglassTopIcon sx={{ fontSize: 16 }} />,
    },
  };
  const cfg = config[status] || { color: 'default', label: status };
  return (
    <Chip
      size='small'
      label={cfg.label}
      color={cfg.color}
      icon={cfg.icon}
      variant='outlined'
      sx={{ fontWeight: 500 }}
    />
  );
};

/**
 * HTTP method chip.
 */
const MethodChip = ({ method }) => {
  const colors = {
    POST: '#2e7d32',
    PUT: '#1565c0',
    PATCH: '#e65100',
    DELETE: '#c62828',
  };
  return (
    <Chip
      size='small'
      label={method}
      sx={{
        backgroundColor: colors[method] || '#757575',
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.7rem',
        minWidth: 56,
      }}
    />
  );
};

/**
 * Expandable row for showing error details.
 */
const ExpandableRow = ({ log, formatTimestamp }) => {
  const [open, setOpen] = useState(false);
  const hasDetails =
    log.errorMessage || log.errorDetails || log.batchFailureCount > 0;

  // Parse batch failure details from JSON
  const parsedFailures = React.useMemo(() => {
    if (!log.errorDetails) return null;
    try {
      return JSON.parse(log.errorDetails);
    } catch {
      return null;
    }
  }, [log.errorDetails]);

  return (
    <>
      <TableRow
        hover
        sx={{
          cursor: hasDetails ? 'pointer' : 'default',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
        onClick={() => hasDetails && setOpen(!open)}
      >
        <TableCell>
          {hasDetails && (
            <IconButton size='small'>
              {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
            {formatTimestamp(log.timestamp)}
          </Typography>
        </TableCell>
        <TableCell>
          <MethodChip method={log.httpMethod} />
        </TableCell>
        <TableCell>
          <Typography variant='body2' fontWeight={500}>
            {log.operationName}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ fontSize: '0.8rem' }}
          >
            {log.endpoint}
          </Typography>
        </TableCell>
        <TableCell align='center'>{log.httpStatusCode}</TableCell>
        <TableCell>
          <StatusChip status={log.status} />
        </TableCell>
        <TableCell>
          <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
            {log.calledBy || '—'}
          </Typography>
        </TableCell>
        <TableCell align='center'>
          {log.batchTotal != null ? (
            <Tooltip
              title={`${log.batchSuccessCount} succeeded, ${log.batchFailureCount} failed`}
            >
              <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                {log.batchSuccessCount}/{log.batchTotal}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant='body2' color='text.secondary'>
              —
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ fontSize: '0.8rem' }}
          >
            {log.durationMs != null ? `${log.durationMs}ms` : '—'}
          </Typography>
        </TableCell>
        <TableCell align='center'>
          <RequestBodyButton requestBody={log.requestBody} />
        </TableCell>
      </TableRow>
      {hasDetails && (
        <TableRow>
          <TableCell colSpan={11} sx={{ py: 0 }}>
            <Collapse in={open} timeout='auto' unmountOnExit>
              <Box sx={{ p: 2 }}>
                {/* Error / Action Needed message */}
                {log.errorMessage && (
                  <Alert
                    severity={
                      log.status === 'ACTION_NEEDED'
                        ? 'warning'
                        : log.status === 'STAGED'
                          ? 'info'
                          : 'error'
                    }
                    sx={{ mb: 1 }}
                  >
                    <AlertTitle>
                      {log.status === 'ACTION_NEEDED'
                        ? 'Action Needed'
                        : log.status === 'STAGED'
                          ? 'Staged — Pending Review'
                          : 'Error'}
                    </AlertTitle>
                    {log.errorMessage}
                  </Alert>
                )}

                {/* Batch failure breakdown */}
                {log.batchTotal != null && log.batchFailureCount > 0 && (
                  <Alert severity='warning' sx={{ mb: 1 }}>
                    <AlertTitle>
                      Batch Operation: {log.batchFailureCount} of{' '}
                      {log.batchTotal} items failed
                    </AlertTitle>
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress
                        variant='determinate'
                        value={(log.batchSuccessCount / log.batchTotal) * 100}
                        color='warning'
                        sx={{ height: 8, borderRadius: 4, mb: 1 }}
                      />
                      <Typography variant='body2'>
                        {log.batchSuccessCount} successful /{' '}
                        {log.batchFailureCount} failed
                      </Typography>
                    </Box>
                  </Alert>
                )}

                {/* Individual failure details (parsed from JSON) */}
                {parsedFailures && typeof parsedFailures === 'object' && (
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant='subtitle2'
                      sx={{ mb: 1, fontWeight: 600 }}
                    >
                      Failed Items:
                    </Typography>
                    <TableContainer
                      sx={{
                        maxHeight: 300,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    >
                      <Table
                        size='small'
                        stickyHeader
                        sx={{ tableLayout: 'auto' }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                            >
                              Composite Key
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                            >
                              Error Reason
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(parsedFailures).map(
                            ([key, reason]) => (
                              <TableRow key={key}>
                                <TableCell>
                                  <Typography
                                    variant='body2'
                                    sx={{
                                      fontFamily: 'monospace',
                                      fontSize: '0.75rem',
                                      wordBreak: 'break-all',
                                    }}
                                  >
                                    {formatCompositeKey(key)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant='body2'
                                    color='error'
                                    sx={{ fontSize: '0.8rem' }}
                                  >
                                    {reason}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Raw error details for non-parseable data */}
                {log.errorDetails && !parsedFailures && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1,
                      backgroundColor: 'grey.100',
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      whiteSpace: 'pre-wrap',
                      maxHeight: 200,
                      overflow: 'auto',
                    }}
                  >
                    {log.errorDetails}
                  </Box>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

// --- Utility functions ---

const makeFormatTimestamp = (formatDateTime) => (ts) => {
  if (!ts) return '—';
  return formatDateTime(ts);
};

/**
 * Format composite key like "date=2025-01-01T00:00,market=5,dest=DEHAM,dep=CNSHA"
 * into a more readable format.
 */
const formatCompositeKey = (key) => {
  if (!key) return key;
  return key
    .split(',')
    .map((part) => {
      const [k, v] = part.split('=');
      const labels = {
        date: 'Date',
        market: 'Market ID',
        dest: 'Destination',
        dep: 'Departure',
      };
      return `${labels[k] || k}: ${v}`;
    })
    .join(' | ');
};

// --- Main Component ---

const HealthMonitor = () => {
  const { isAdmin, isKeyUser } = useUserRoles();
  const canCleanup = isAdmin || isKeyUser;
  const { formatDateTime } = useDateFormat();
  const formatTimestamp = useCallback(
    (ts) => makeFormatTimestamp(formatDateTime)(ts),
    [formatDateTime]
  );

  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [cleanupModalOpen, setCleanupModalOpen] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalElements, setTotalElements] = useState(0);
  const [filter, setFilter] = useState('all'); // "all" | "failures" | "action_needed"
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error',
  });

  const fetchLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const params = { page, size: rowsPerPage, filter };
      const response = await getHealthMonitorLogs(params);
      setLogs(response.content || []);
      setTotalElements(response.totalElements || 0);
    } catch (error) {
      console.error('Failed to fetch health monitor logs:', error);
      setLogs([]);
      setSnackbar({
        open: true,
        message: 'Failed to fetch health monitor logs.',
        severity: 'error',
      });
    } finally {
      setLoadingLogs(false);
    }
  }, [page, rowsPerPage, filter]);

  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const data = await getHealthMonitorSummary();
      setSummary(data);
    } catch (error) {
      console.error('Failed to fetch health summary:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch health monitor summary.',
        severity: 'error',
      });
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  const refreshAll = useCallback(() => {
    fetchLogs();
    fetchSummary();
  }, [fetchLogs, fetchSummary]);

  // Initial load and filter/page changes
  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(refreshAll, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshAll]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setFilter(newFilter);
      setPage(0);
    }
  };

  const handleCleanup = async () => {
    setCleaningUp(true);
    try {
      await postHealthMonitorCleanup();
      setSnackbar({
        open: true,
        message: 'Log cleanup completed successfully.',
        severity: 'success',
      });
      setCleanupModalOpen(false);
      refreshAll();
    } catch (error) {
      console.error('Failed to clean up logs:', error);
      setSnackbar({
        open: true,
        message: 'Failed to clean up logs.',
        severity: 'error',
      });
    } finally {
      setCleaningUp(false);
    }
  };

  return (
    <Stack spacing={3}>
      {/* KPI Summary Cards */}
      <Box>
        {loadingSummary && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <KpiCard
              title='Total Calls (24h)'
              value={summary?.totalCalls24h ?? '—'}
              color='text.primary'
              icon={<MonitorHeartOutlinedIcon sx={{ fontSize: 32 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <KpiCard
              title='Successful'
              value={summary?.successes24h ?? '—'}
              color='success.main'
              icon={<CheckCircleOutlinedIcon sx={{ fontSize: 32 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <KpiCard
              title='Partial Failures'
              value={summary?.partialSuccesses24h ?? '—'}
              color='warning.main'
              icon={<WarningAmberOutlinedIcon sx={{ fontSize: 32 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <KpiCard
              title='Action Needed'
              value={summary?.actionNeeded24h ?? '—'}
              color='info.main'
              icon={<InfoOutlinedIcon sx={{ fontSize: 32 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <KpiCard
              title='Failures'
              value={summary?.failures24h ?? '—'}
              color='error.main'
              icon={<ErrorOutlinedIcon sx={{ fontSize: 32 }} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <KpiCard
              title='Success Rate'
              value={summary ? `${summary.successRate24h}%` : '—'}
              color={
                !summary
                  ? 'text.secondary'
                  : summary.successRate24h >= 95
                    ? 'success.main'
                    : summary.successRate24h >= 80
                      ? 'warning.main'
                      : 'error.main'
              }
              icon={
                summary && summary.successRate24h >= 95 ? (
                  <CheckCircleOutlinedIcon sx={{ fontSize: 32 }} />
                ) : (
                  <WarningAmberOutlinedIcon sx={{ fontSize: 32 }} />
                )
              }
            />
          </Grid>
        </Grid>
      </Box>

      {/* Log Table */}
      <CardContainer>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <CardHeader title='API Call Logs' icon={MonitorHeartOutlinedIcon} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ToggleButtonGroup
              color='primary'
              value={filter}
              exclusive
              onChange={handleFilterChange}
              size='small'
            >
              <ToggleButton value='all' sx={{ minWidth: 100 }}>
                All
              </ToggleButton>
              <ToggleButton value='failures' sx={{ minWidth: 100 }}>
                Failures Only
              </ToggleButton>
              <ToggleButton value='action_needed' sx={{ minWidth: 100 }}>
                Action Only
              </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip
              title={autoRefresh ? 'Auto-refresh ON (30s)' : 'Auto-refresh OFF'}
            >
              <Chip
                label={autoRefresh ? 'Auto' : 'Manual'}
                color={autoRefresh ? 'success' : 'default'}
                onClick={() => setAutoRefresh(!autoRefresh)}
                variant={autoRefresh ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer' }}
              />
            </Tooltip>
            <Tooltip title='Refresh now'>
              <IconButton onClick={refreshAll} size='small'>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {canCleanup && (
              <Tooltip title='Clean up logs older than 30 days' sx={{ ml: 2 }}>
                <Button
                  variant='outlined'
                  color='error'
                  size='small'
                  startIcon={<DeleteSweepIcon />}
                  onClick={() => setCleanupModalOpen(true)}
                >
                  Cleanup
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>

        {loadingLogs && <LinearProgress sx={{ mb: 1 }} />}

        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 80 }}>
                  Method
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Operation</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Endpoint</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align='center'>
                  Status Code
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Result</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Called By</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align='center'>
                  Batch
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align='center'>
                  Request Body
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && !loadingLogs ? (
                <TableRow>
                  <TableCell colSpan={11} align='center'>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ py: 4 }}
                    >
                      {filter === 'failures'
                        ? 'No failures found — all systems operational!'
                        : filter === 'action_needed'
                          ? 'No action needed — everything is in order!'
                          : 'No API call logs found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <ExpandableRow
                    key={log.id}
                    log={log}
                    formatTimestamp={formatTimestamp}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component='div'
          count={totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </CardContainer>

      <DoubleConfirmModal
        title='Confirm Log Cleanup'
        open={cleanupModalOpen}
        onClose={() => setCleanupModalOpen(false)}
        onConfirm={handleCleanup}
        onCancel={() => setCleanupModalOpen(false)}
        confirmText='Delete'
        isDisabled={cleaningUp}
        contextText='This will permanently delete all API call logs older than 30 days. This action cannot be undone.'
      />

      <ExpSnackbarAlerts
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Stack>
  );
};

export default HealthMonitor;
